import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../App';
import axios from 'axios';
import { API } from '../../utils/config';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import Ross1000GroupCredentialsManager from '../../components/Ross1000GroupCredentialsManager';
import IstatBulkSendButton from '../../components/IstatBulkSendButton';
import IstatCoverageReport from '../../components/IstatCoverageReport';
import { IstatNetworkOption, IstatNetworkConfirm } from '../../components/IstatNetworkControls';
import { IstatNetworkReport } from '../../components/IstatNetworkReport';
import { IstatMobileSort } from '../../components/IstatMobileSort';
import './IstatDashboard.css';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Search,
  Filter,
  ArrowUpDown,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Send,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Calendar,
  BarChart3,
  Settings,
  ChevronDown,
  ChevronUp,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

const IstatDashboard = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = ['status', 'coverage', 'group-credentials'].includes(searchParams.get('tab'))
    ? searchParams.get('tab')
    : 'status';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [data, setData] = useState(null);
  const [submitting, setSubmitting] = useState(null);
  const [expandedProperty, setExpandedProperty] = useState(null);
  const [networkProperty, setNetworkProperty] = useState(null);
  const [pendingNetworkTest, setPendingNetworkTest] = useState(null);
  const [networkReport, setNetworkReport] = useState(null);
  const sendInFlight = useRef(false);
  const fetchSequence = useRef(0);
  
  // Default: mese precedente
  const [month, setMonth] = useState(() => {
    const now = new Date();
    const prevMonth = now.getMonth(); // 0-indexed, so current getMonth() = prev month (1-indexed)
    return prevMonth === 0 ? 12 : prevMonth;
  });
  const [year, setYear] = useState(() => {
    const now = new Date();
    return now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  });
  const [filters, setFilters] = useState({
    search: '',
    region: 'all',
    status: 'all',
    onlyWithMovements: false
  });
  const [sortConfig, setSortConfig] = useState({ key: 'status', direction: 'asc' });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchData();
  }, [month, year]);

  const fetchData = async () => {
    const request = ++fetchSequence.current;
    try {
      setLoading(true);
      setLoadError(null);
      const token = localStorage.getItem('hw_token');
      const res = await axios.get(
        `${API}/api/istat/properties-status?month=${month}&year=${year}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (request !== fetchSequence.current) return;
      setData(res.data);
    } catch {
      if (request !== fetchSequence.current) return;
      setData(null);
      setLoadError('Stato degli invii non disponibile.');
      toast.error('Errore nel caricamento dati ISTAT');
    } finally {
      if (request === fetchSequence.current) setLoading(false);
    }
  };

  // Invio giornaliero per un immobile
  const handleSubmitDay = async (propertyId, targetDate, confirmed = false) => {
    if (sendInFlight.current) return;
    const withNetwork = networkProperty === propertyId;
    if (withNetwork && !confirmed) {
      setPendingNetworkTest({ propertyId, targetDate });
      return;
    }
    sendInFlight.current = true;
    setPendingNetworkTest(null);
    setNetworkProperty(null); // Single-use consent, including failed attempts.
    if (withNetwork) setNetworkReport(null);
    try {
      setSubmitting(`${propertyId}-${targetDate}`);
      const token = localStorage.getItem('hw_token');
      const res = await axios.post(
        `${API}/api/flussi-turistici/submit/daily/${propertyId}?target_date=${targetDate}${withNetwork ? '&network_diagnostics=true' : ''}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      if (res.data.network_diagnostics) setNetworkReport(res.data.network_diagnostics);
      if (res.data.status === 'success') {
        toast.success(`Inviato con successo per ${targetDate}: ${res.data.records_submitted} record`);
        fetchData();
      } else if (res.data.status === 'in_progress') {
        toast.info(
          <span data-testid={`istat-pending-result-${propertyId}`}>{res.data.message}</span>,
          { id: `istat-pending-${propertyId}`, duration: 10000 }
        );
      } else {
        toast.error(`Errore ${targetDate}: ${withNetwork ? 'Consulta la diagnosi di rete e lo storico ISTAT.' : res.data.message || 'Errore sconosciuto'}`);
        if (!withNetwork) console.error(`[ISTAT] Errore per ${targetDate}:`, res.data);
      }
    } catch (error) {
      if (error.response?.data?.network_diagnostics) setNetworkReport(error.response.data.network_diagnostics);
      const msg = withNetwork ? 'Invio non riuscito: consulta la diagnosi di rete e lo storico ISTAT.' :
        error.response?.data?.detail || error.response?.data?.message || error.message || 'Errore di connessione';
      toast.error(`Errore ${targetDate}: ${msg}`);
      if (!withNetwork) console.error(`[ISTAT] Exception per ${targetDate}:`, msg);
    } finally {
      sendInFlight.current = false;
      setSubmitting(null);
    }
  };

  // Invio tutti i giorni pendenti
  const handleSubmitAllPending = async (propertyId, pendingDetail) => {
    if (sendInFlight.current) return;
    if (!pendingDetail || pendingDetail.length === 0) {
      toast.warning('Nessun giorno pendente da inviare');
      return;
    }
    sendInFlight.current = true;
    setSubmitting(`${propertyId}-all`);
    let successCount = 0;
    let errorCount = 0;
    let lastError = '';
    
    toast.info(`Invio ${pendingDetail.length} giorni in corso...`);
    
    const allDates = pendingDetail.map(d => d.date);
    for (const dateStr of allDates) {
      try {
        const token = localStorage.getItem('hw_token');
        const res = await axios.post(
          `${API}/api/flussi-turistici/submit/daily/${propertyId}?target_date=${dateStr}`,
          {},
          { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
        );
        if (res.data.status === 'success') {
          successCount++;
        } else if (res.data.status === 'in_progress') {
          toast.info(
            <span data-testid={`istat-bulk-pending-result-${propertyId}`}>
              {dateStr}: esito ancora da verificare. Gli altri giorni non sono stati avviati; consulta lo storico prima di riprovare.
            </span>,
            { id: `istat-bulk-pending-${propertyId}`, duration: 10000 }
          );
          break;
        } else {
          errorCount++;
          lastError = res.data.message || 'Errore sconosciuto';
          console.error(`[ISTAT] Errore per ${dateStr}:`, res.data.message);
        }
      } catch (err) {
        errorCount++;
        lastError = err.response?.data?.detail || err.message || 'Errore di rete';
        console.error(`[ISTAT] Exception per ${dateStr}:`, lastError);
      }
    }
    
    if (successCount > 0) toast.success(`${successCount}/${allDates.length} giorni inviati con successo`);
    if (errorCount > 0) toast.error(`${errorCount} giorni con errore: ${lastError}`);
    sendInFlight.current = false;
    setSubmitting(null);
    fetchData();
  };

  // Regioni uniche
  const uniqueRegions = useMemo(() => {
    if (!data?.properties) return [];
    const regions = [...new Set(data.properties.map(p => p.region).filter(Boolean))];
    return regions.sort();
  }, [data]);

  // Filtro e ordinamento
  const filteredProperties = useMemo(() => {
    if (!data?.properties) return [];
    let result = [...data.properties];

    if (filters.search) {
      const s = filters.search.toLowerCase();
      result = result.filter(p =>
        p.property_name?.toLowerCase().includes(s) ||
        p.city?.toLowerCase().includes(s) ||
        p.cir?.toLowerCase().includes(s)
      );
    }
    if (filters.region !== 'all') {
      result = result.filter(p => p.region === filters.region);
    }
    if (filters.status !== 'all') {
      result = result.filter(p => p.status === filters.status);
    }
    if (filters.onlyWithMovements) {
      result = result.filter(p => p.days_with_movements > 0 || p.pending_days > 0);
    }

    // Ordinamento
    const statusOrder = { da_inviare: 0, nessun_movimento: 1, aggiornato: 2, non_configurato: 3 };
    result.sort((a, b) => {
      let aVal, bVal;
      if (sortConfig.key === 'status') {
        aVal = statusOrder[a.status] ?? 9;
        bVal = statusOrder[b.status] ?? 9;
      } else {
        aVal = a[sortConfig.key] ?? '';
        bVal = b[sortConfig.key] ?? '';
      }
      if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = (bVal || '').toLowerCase(); }
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [data, filters, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getStatusBadge = (status, propertyId) => {
    const badgeTestId = `istat-badge-${propertyId}`;
    switch (status) {
      case 'aggiornato':
        return <Badge className="bg-green-100 text-green-700" data-testid={badgeTestId}><CheckCircle2 className="h-3 w-3 mr-1" />Aggiornato</Badge>;
      case 'da_inviare':
        return <Badge className="bg-amber-100 text-amber-700" data-testid={badgeTestId}><Clock className="h-3 w-3 mr-1" />Da inviare</Badge>;
      case 'nessun_movimento':
        return <Badge className="bg-blue-100 text-blue-700" data-testid={badgeTestId}><FileText className="h-3 w-3 mr-1" />Nessun movimento</Badge>;
      case 'non_configurato':
        return <Badge variant="outline" className="text-slate-400" data-testid={badgeTestId}><Settings className="h-3 w-3 mr-1" />Non configurato</Badge>;
      default:
        return <Badge variant="outline" data-testid={badgeTestId}>{status}</Badge>;
    }
  };

  const monthNames = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

  const navigateMonth = (direction) => {
    let newMonth = month + direction;
    let newYear = year;
    if (newMonth < 1) { newMonth = 12; newYear--; }
    if (newMonth > 12) { newMonth = 1; newYear++; }
    setMonth(newMonth);
    setYear(newYear);
  };

  const summary = data?.summary || {};

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="istat-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestione ISTAT</h1>
          <p className="text-slate-500">Stato invii flussi turistici per immobile</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {user?.role === 'admin' && !user?.is_external_tenant && <Button variant="outline" asChild><Link to="/admin/istat/connessioni" data-testid="istat-network-only-link">Verifica connessioni · senza invio</Link></Button>}
          <IstatBulkSendButton
            disabled={Boolean(submitting) || loading || Boolean(loadError) || !data}
            month={month}
            year={year}
            availableRegions={(() => {
              const map = new Map();
              (data?.properties || []).forEach(p => {
                const raw = (p.region || '').trim();
                if (!raw) return;
                // Skip non-real region values (es. "Da completare", "LAGHI")
                if (raw.toLowerCase() === 'da completare' || raw.toLowerCase() === 'laghi') return;
                // Title-case dedup
                const key = raw.toLowerCase();
                if (!map.has(key)) {
                  // Capitalize first letter, lowercase the rest (keep accents/dashes)
                  const titled = raw.length > 1
                    ? raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase()
                    : raw.toUpperCase();
                  map.set(key, titled);
                }
              });
              return Array.from(map.values()).sort();
            })()}
            onCompleted={fetchData}
          />
          <Button onClick={fetchData} variant="outline" disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSearchParams({ tab: v }); }} className="w-full">
        <TabsList className="mb-4 flex h-auto flex-wrap justify-start gap-1" data-testid="istat-tabs">
          <TabsTrigger value="status" data-testid="tab-istat-status">📊 Stato Invii</TabsTrigger>
          <TabsTrigger value="coverage" data-testid="tab-istat-coverage">📋 Coverage Report</TabsTrigger>
          <TabsTrigger value="group-credentials" data-testid="tab-istat-group-creds">🔐 Credenziali di Gruppo</TabsTrigger>
        </TabsList>

        <TabsContent value="status" className="space-y-6 mt-0">

      {loadError && <div role="alert" data-testid="istat-load-error" className="text-sm text-red-700">{loadError}</div>}

      <IstatNetworkConfirm pending={pendingNetworkTest} onCancel={() => setPendingNetworkTest(null)}
        onConfirm={() => handleSubmitDay(pendingNetworkTest.propertyId, pendingNetworkTest.targetDate, true)} />
      <IstatNetworkReport report={networkReport} onClose={() => setNetworkReport(null)} />

      {/* Periodo con navigazione mese */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Periodo:</span>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" onClick={() => navigateMonth(-1)} data-testid="prev-month-btn">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="px-4 py-1.5 min-w-[180px] text-center font-medium text-slate-800" data-testid="current-period">
                {monthNames[month - 1]} {year}
              </div>
              <Button variant="outline" size="sm" onClick={() => navigateMonth(1)} data-testid="next-month-btn">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            <div className="ml-auto flex items-center gap-2">
              <Button
                variant={showFilters ? 'default' : 'outline'}
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                data-testid="toggle-filters"
              >
                <Filter className="h-4 w-4 mr-1" />
                Filtri
              </Button>
            </div>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4 pt-4 border-t">
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Cerca</label>
                <Input
                  placeholder="Nome, città, CIR..."
                  value={filters.search}
                  onChange={(e) => { setFilters({ ...filters, search: e.target.value }); }}
                  data-testid="search-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Regione</label>
                <Select value={filters.region} onValueChange={(v) => { setFilters({ ...filters, region: v }); }}>
                  <SelectTrigger data-testid="region-filter">
                    <SelectValue placeholder="Tutte" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte le regioni</SelectItem>
                    {uniqueRegions.map(r => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Stato ISTAT</label>
                <Select value={filters.status} onValueChange={(v) => { setFilters({ ...filters, status: v }); }}>
                  <SelectTrigger data-testid="status-filter">
                    <SelectValue placeholder="Tutti" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti gli stati</SelectItem>
                    <SelectItem value="da_inviare">Da inviare</SelectItem>
                    <SelectItem value="aggiornato">Aggiornato</SelectItem>
                    <SelectItem value="nessun_movimento">Nessun movimento</SelectItem>
                    <SelectItem value="non_configurato">Non configurato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button
                  variant={filters.onlyWithMovements ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setFilters({ ...filters, onlyWithMovements: !filters.onlyWithMovements }); }}
                  className="w-full"
                  data-testid="only-movements-btn"
                >
                  <BarChart3 className="h-4 w-4 mr-1" />
                  Solo con movimenti
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statistiche */}
      {!loadError && <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card>
          <CardContent className="p-4 text-center">
            <p data-testid="istat-summary-total" className="text-2xl font-bold text-slate-900">{summary.total || 0}</p>
            <p className="text-xs text-slate-500">Totale immobili</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p data-testid="istat-summary-configured" className="text-2xl font-bold text-blue-600">{summary.configured || 0}</p>
            <p className="text-xs text-slate-500">Configurati ISTAT</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p data-testid="istat-summary-pending" className="text-2xl font-bold text-amber-600">{summary.da_inviare || 0}</p>
            <p className="text-xs text-slate-500">Da inviare</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p data-testid="istat-summary-updated" className="text-2xl font-bold text-green-600">{summary.aggiornato || 0}</p>
            <p className="text-xs text-slate-500">Aggiornati</p>
          </CardContent>
        </Card>
        <Card className="col-span-2 md:col-span-1">
          <CardContent className="p-4 text-center">
            <p data-testid="istat-summary-unconfigured" className="text-2xl font-bold text-slate-400">{summary.non_configurato || 0}</p>
            <p className="text-xs text-slate-500">Non configurati</p>
          </CardContent>
        </Card>
      </div>}

      {/* Tabella */}
      <Card>
        <CardContent className="p-0">
          <IstatMobileSort column={sortConfig.key} ascending={sortConfig.direction === 'asc'}
            onColumn={key => setSortConfig({ key, direction: 'asc' })}
            onDirection={() => setSortConfig(previous => ({ ...previous, direction: previous.direction === 'asc' ? 'desc' : 'asc' }))} />
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              <span className="ml-3 text-slate-600">Caricamento...</span>
            </div>
          ) : loadError ? null : (
            <>
              <div className="overflow-x-auto">
            <Table className="istat-status-table" data-testid="istat-status-table">
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="cursor-pointer hover:bg-slate-100" onClick={() => handleSort('property_name')}>
                        <div className="flex items-center gap-1">
                          Immobile
                          <ArrowUpDown className="h-3 w-3" />
                        </div>
                      </TableHead>
                      <TableHead className="cursor-pointer hover:bg-slate-100" onClick={() => handleSort('region')}>
                        <div className="flex items-center gap-1">
                          Regione
                          <ArrowUpDown className="h-3 w-3" />
                        </div>
                      </TableHead>
                      <TableHead>Portale</TableHead>
                      <TableHead>CIR</TableHead>
                      <TableHead className="text-center cursor-pointer hover:bg-slate-100" onClick={() => handleSort('total_days')}>
                        <div className="flex items-center justify-center gap-1">
                          Giorni
                          <ArrowUpDown className="h-3 w-3" />
                        </div>
                      </TableHead>
                      <TableHead className="text-center cursor-pointer hover:bg-slate-100" onClick={() => handleSort('sent_days')}>
                        <div className="flex items-center justify-center gap-1">
                          Inviati
                          <ArrowUpDown className="h-3 w-3" />
                        </div>
                      </TableHead>
                      <TableHead className="text-center cursor-pointer hover:bg-slate-100" onClick={() => handleSort('pending_days')}>
                        <div className="flex items-center justify-center gap-1">
                          Pendenti
                          <ArrowUpDown className="h-3 w-3" />
                        </div>
                      </TableHead>
                      <TableHead className="cursor-pointer hover:bg-slate-100" onClick={() => handleSort('status')}>
                        <div className="flex items-center gap-1">
                          Stato
                          <ArrowUpDown className="h-3 w-3" />
                        </div>
                      </TableHead>
                      <TableHead>Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProperties.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                          Nessun immobile trovato con i filtri selezionati
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredProperties.map((prop) => (
                        <React.Fragment key={prop.property_id}>
                          <TableRow
                            className={`hover:bg-slate-50/80 ${
                              prop.status === 'da_inviare' ? 'border-l-4 border-l-amber-400 bg-amber-50/30' :
                              prop.status === 'aggiornato' ? 'border-l-4 border-l-green-500 bg-green-50/30' :
                              ''
                            }`}
                            data-testid={`istat-row-${prop.property_id}`}
                          >
                            <TableCell data-label="Immobile" data-testid={`istat-property-${prop.property_id}`}>
                              <div>
                                <p className="font-medium text-sm">{prop.property_name}</p>
                                <p className="text-[10px] text-slate-400">{prop.city}{prop.province ? ` (${prop.province})` : ''}</p>
                              </div>
                            </TableCell>
                            <TableCell data-label="Regione" data-testid={`istat-region-${prop.property_id}`}>
                              <span className="text-sm">{prop.region || '-'}</span>
                            </TableCell>
                            <TableCell data-label="Portale e frequenza" data-testid={`istat-portal-${prop.property_id}`}>
                              <span className="text-xs text-slate-600">{prop.portal}</span>
                              <p className="text-[10px] text-slate-400">{prop.frequency}</p>
                            </TableCell>
                            <TableCell data-label="CIR" data-testid={`istat-cir-${prop.property_id}`}>
                              <span className="text-xs font-mono">{prop.cir || '-'}</span>
                            </TableCell>
                            <TableCell data-label="Giorni totali" data-testid={`istat-days-${prop.property_id}`} className="text-center">
                              <div className="flex flex-col items-center">
                                <span className="font-medium">{prop.total_days}</span>
                                {(prop.days_with_movements > 0 || prop.days_closed > 0) && (
                                  <div className="flex gap-1 mt-0.5">
                                    {prop.days_with_movements > 0 && (
                                      <span className="text-[9px] text-blue-600" title="Giorni con arrivi/partenze">{prop.days_with_movements}mov</span>
                                    )}
                                    {prop.days_closed > 0 && (
                                      <span className="text-[9px] text-red-500" title="Giorni chiusi">{prop.days_closed}ch</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell data-label="Inviati" data-testid={`istat-sent-${prop.property_id}`} className="text-center">
                              {prop.sent_days > 0 ? (
                                <span className="text-green-600 font-medium">{prop.sent_days}</span>
                              ) : (
                                <span className="text-slate-300">0</span>
                              )}
                            </TableCell>
                            <TableCell data-label="Da inviare" data-testid={`istat-pending-${prop.property_id}`} className="text-center">
                              {prop.pending_days > 0 ? (
                                <span className="text-amber-600 font-bold">{prop.pending_days}</span>
                              ) : (
                                <span className="text-slate-300">0</span>
                              )}
                            </TableCell>
                            <TableCell data-label="Stato" data-testid={`istat-status-${prop.property_id}`}>
                              {getStatusBadge(prop.status, prop.property_id)}
                            </TableCell>
                            <TableCell data-label="Azioni" data-testid={`istat-actions-${prop.property_id}`}>
                              <div className="flex items-center gap-1">
                                {prop.pending_days > 0 && prop.has_credentials && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleSubmitAllPending(prop.property_id, prop.pending_detail)}
                                    disabled={Boolean(submitting)}
                                    className="h-7 px-2 text-[10px] border-amber-400 text-amber-700 hover:bg-amber-50"
                                    data-testid={`submit-all-${prop.property_id}`}
                                    title={`Invia ${prop.pending_days} giorni pendenti`}
                                  >
                                    {submitting === `${prop.property_id}-all` ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <Send className="h-3 w-3 mr-1" />
                                    )}
                                    Invia tutto
                                  </Button>
                                )}
                                {prop.pending_detail?.length > 0 && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => { setExpandedProperty(expandedProperty === prop.property_id ? null : prop.property_id); setNetworkProperty(null); }}
                                    className="h-7 px-2"
                                    data-testid={`expand-${prop.property_id}`}
                                  >
                                    {expandedProperty === prop.property_id ? (
                                      <ChevronUp className="h-4 w-4" />
                                    ) : (
                                      <ChevronDown className="h-4 w-4" />
                                    )}
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                          {/* Expanded: date pendenti */}
                          {expandedProperty === prop.property_id && prop.pending_detail?.length > 0 && (
                            <TableRow>
                              <TableCell colSpan={9} className="bg-slate-50 p-3">
                                <div className="space-y-2">
                                  <p className="text-xs font-medium text-slate-600">Giorni non inviati ({prop.pending_days} totali):</p>
                                  {(prop.region || '').trim().toLowerCase() === 'calabria' && prop.has_credentials && (
                                    <IstatNetworkOption propertyId={prop.property_id} checked={networkProperty === prop.property_id}
                                      disabled={Boolean(submitting)} onChange={checked => setNetworkProperty(checked ? prop.property_id : null)} />
                                  )}
                                  <div className="flex flex-wrap gap-2">
                                    {prop.pending_detail.map(({ date: dateStr, type: dayType }) => (
                                      <div key={dateStr} className={`flex items-center gap-1 border rounded-lg px-3 py-1.5 ${
                                        dayType === 'movimento' ? 'bg-blue-50 border-blue-200' :
                                        dayType === 'chiuso' ? 'bg-red-50 border-red-200' :
                                        'bg-white border-slate-200'
                                      }`}>
                                        <span className="text-sm font-medium">
                                          {new Date(dateStr + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                                        </span>
                                        <span className={`text-[9px] px-1 rounded ${
                                          dayType === 'movimento' ? 'bg-blue-100 text-blue-700' :
                                          dayType === 'chiuso' ? 'bg-red-100 text-red-700' :
                                          'bg-slate-100 text-slate-500'
                                        }`}>
                                          {dayType === 'movimento' ? 'MOV' : dayType === 'chiuso' ? 'CHIUSO' : 'LIBERO'}
                                        </span>
                                        {prop.has_credentials && (
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => handleSubmitDay(prop.property_id, dateStr)}
                                            disabled={Boolean(submitting)}
                                            title="Invia i movimenti di questa giornata"
                                            aria-label={`Invia i movimenti del ${dateStr}`}
                                            className="h-6 px-1.5 ml-1"
                                            data-testid={`submit-day-${prop.property_id}-${dateStr}`}
                                          >
                                            {submitting === `${prop.property_id}-${dateStr}` ? (
                                              <Loader2 className="h-3 w-3 animate-spin" />
                                            ) : (
                                              <Send className="h-3 w-3 text-amber-600" />
                                            )}
                                          </Button>
                                        )}
                                      </div>
                                    ))}
                                    {prop.pending_days > 15 && (
                                      <span className="text-xs text-slate-400 self-center">...e altri {prop.pending_days - 15} giorni</span>
                                    )}
                                  </div>
                                  <div className="flex gap-4 text-[10px] text-slate-400 mt-1">
                                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-blue-300"></span>Movimento (arrivi/partenze)</span>
                                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-red-300"></span>Chiuso</span>
                                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-slate-300"></span>Libero</span>
                                  </div>
                                  {!prop.has_credentials && (
                                    <p data-testid={`istat-send-unavailable-${prop.property_id}`} className="text-xs text-red-500 flex items-center gap-1">
                                      <AlertTriangle className="h-3 w-3" />
                                      Invio non abilitato. Verifica delega e configurazione del portale in Immobili &gt; Configurazione.
                                    </p>
                                  )}
                                  {prop.last_sent_at && (
                                    <p className="text-[10px] text-slate-400">
                                      Ultimo invio: {new Date(prop.last_sent_at).toLocaleString('it-IT')}
                                    </p>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Conteggio totale */}
              <div className="px-4 py-3 border-t text-sm text-slate-500">
                {filteredProperties.length} immobili
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Info frequenze regionali */}
      {data?.regional_frequencies && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Frequenze invio per regione
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              {Object.entries(data.regional_frequencies)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([region, info]) => (
                <div key={region} className="flex items-center justify-between p-2 rounded bg-slate-50">
                  <span className="font-medium">{region}</span>
                  <Badge variant="outline" className={`text-[10px] ${
                    info.frequency === 'giornaliero' ? 'border-blue-300 text-blue-700' : 'border-slate-300 text-slate-600'
                  }`}>
                    {info.frequency}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
        </TabsContent>

        <TabsContent value="coverage" className="space-y-6 mt-0">
          <IstatCoverageReport month={month} year={year} />
        </TabsContent>

        <TabsContent value="group-credentials" className="space-y-6 mt-0">
          <Ross1000GroupCredentialsManager />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default IstatDashboard;
